/**
 * What a timeline lane can control in OPVisual (2026-11-02, operator: "when adding a lane, opvisual should be a
 * group/folder and then in the sub menu show all controls", "the knobs needs names not numbers"). The verbs and their
 * fields are OPVisual's CONTRACT.md s4b; OPBridge keeps no list of them.
 *
 * Every control here is an `automation` lane with `opvControl`, drawn and edited like any other curve:
 *   value   the curve is the control: 0-127 scaled to `min`..`max`, sampled like a knob lane.
 *   toggle  the top half of the curve is on, the bottom half off; a cue only where it crosses.
 *   fire    each point fires once.
 *
 * EFFECT ON A CELL (2026-11-02, OPVisual's ask, its DEC-177 and CONTRACT s4b `fx` / `fx_release`; operator: "i want
 * opbuttons to be able to control fx on a clip, like turning it on flow and change the intensity"): a lane per effect
 * (toggle: the top half holds that effect on the cell, the bottom half lets it go) and an amount lane (value: 0..1, the
 * effect's main fader), each aimed at one grid cell (`opvControl.cell`). OPVisual holds the effect through re-fires and
 * never saves it; it cannot see a timeline stop, so every one of them lets its cell go at a stop.
 *
 * Left out on purpose: `panic`, `reload_library`, `operator_screen` (not part of a show); `text_fire` and `layer_blend`
 * (they need words or a mode a curve cannot hold); `chat` (data, not a control); the retired chain verbs.
 */

export interface VizKnobs { id: string; name: string; knobs: string[] }
/** Each visualiser's knobs, in order (OPVisual `app/src/viz/<module>.rs::MACROS`, read 2026-11-02). OPVisual does not
 *  send these, so a new visualiser needs a row here. */
export const VIZ_KNOB_NAMES: readonly VizKnobs[] = [
  { id: 'viz.genesis', name: 'Genesis', knobs: ['Life', 'Glow', 'Depth', 'Current', 'Light', 'Pulse'] },
  { id: 'viz.chrome', name: 'Liquid Chrome', knobs: ['Liquid', 'Melt', 'Polish', 'Light', 'Backdrop', 'Pulse'] },
  { id: 'viz.nebula_fluid', name: 'Nebula Fluid', knobs: ['Swirl', 'Ink', 'Trails', 'Mirror', 'Space', 'Pulse'] },
  { id: 'viz.laser_rig', name: 'Laser Rig', knobs: ['Beams', 'Speed', 'Haze', 'Change', 'Stage', 'Pulse', 'Crowd'] },
];

/** A knob's name on one visualiser (`macro` from 1), or null when the asset is not a known visualiser or has no such knob. */
export function vizKnobName(assetId: string | null | undefined, macro: number): string | null {
  return VIZ_KNOB_NAMES.find((v) => v.id === assetId)?.knobs[macro - 1] ?? null;
}
/** What knob `macro` is called across the visualisers, each name once: "Life / Liquid / Swirl / Beams". */
export function vizKnobNames(macro: number): string {
  return [...new Set(VIZ_KNOB_NAMES.flatMap((v) => { const k = v.knobs[macro - 1]; return k ? [k] : []; }))].join(' / ');
}

export type OpvControlMode = 'value' | 'toggle' | 'fire';
export interface OpvControlDef {
  /** Unique: the verb, with the `look` parameter where it has one. */
  key: string;
  label: string;
  verb: string;
  mode: OpvControlMode;
  param?: string;
  /** It acts on one layer, chosen when the lane is added. */
  layered?: true;
  /** A `value` control's range (default 0..1). */
  min?: number;
  max?: number;
  /** One plain line for the lane. */
  does: string;
  /** What a stop sends so nothing stays stuck: only where being left there would look broken. */
  atStop?: Record<string, unknown>;
  /** It acts on one grid cell (`opvControl.cell`), chosen when the lane is added: the `fx` controls. */
  cell?: true;
}

export const OPV_PICTURE_CONTROLS: readonly OpvControlDef[] = [
  { key: 'master_opacity', label: 'Master opacity', verb: 'master_opacity', mode: 'value', does: 'Fades the whole picture', atStop: { value: 1 } },
  { key: 'look.brightness', label: 'Brightness', verb: 'look', param: 'brightness', mode: 'value', does: 'The picture\'s brightness' },
  { key: 'look.hue', label: 'Hue', verb: 'look', param: 'hue', mode: 'value', does: 'Turns the picture\'s hue' },
  { key: 'look.sat', label: 'Saturation', verb: 'look', param: 'sat', mode: 'value', does: 'The picture\'s saturation' },
  { key: 'look.bloom', label: 'Bloom', verb: 'look', param: 'bloom', mode: 'value', does: 'The picture\'s bloom' },
  { key: 'crowd', label: 'Crowd', verb: 'crowd', mode: 'value', does: 'What clip effects riding Crowd follow' },
  { key: 'blackout', label: 'Blackout', verb: 'blackout', mode: 'toggle', does: 'Top half blacks the picture out', atStop: { on: false } },
  { key: 'cam_shake', label: 'Camera shake', verb: 'cam_shake', mode: 'toggle', does: 'Top half shakes the camera', atStop: { on: false } },
  { key: 'stream_safe', label: 'Stream safe', verb: 'stream_safe', mode: 'toggle', does: 'Top half turns Stream safe on' },
  { key: 'chat_wall', label: 'Chat wall', verb: 'chat_wall', mode: 'toggle', does: 'Top half puts the chat wall up' },
  { key: 'stop_all', label: 'Stop all', verb: 'stop_all', mode: 'fire', does: 'Each point stops every layer' },
  { key: 'viz_variation', label: 'New take', verb: 'viz_variation', mode: 'fire', does: 'Each point starts a new take of the visualiser' },
];

export const OPV_LAYER_CONTROLS: readonly OpvControlDef[] = [
  { key: 'layer_opacity', label: 'Opacity', verb: 'layer_opacity', mode: 'value', layered: true, does: 'Fades this layer', atStop: { value: 1 } },
  { key: 'layer_speed', label: 'Speed', verb: 'layer_speed', mode: 'value', layered: true, min: 0.05, max: 2, does: 'This layer\'s speed, the middle about normal', atStop: { value: 1 } },
  { key: 'layer_enabled', label: 'On / off', verb: 'layer_enabled', mode: 'toggle', layered: true, does: 'Top half has this layer on', atStop: { on: true } },
  { key: 'layer_clear', label: 'Clear', verb: 'layer_clear', mode: 'fire', layered: true, does: 'Each point clears this layer' },
];

export const OPV_LAYERS = [1, 2, 3, 4, 5] as const;
export const OPV_COLUMNS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/** Effect on a cell: `param` is the effect's word on the wire (OPVisual's `fx` names), or `amount`. */
export const OPV_CELL_FX: readonly OpvControlDef[] = [
  { key: 'fx.flow', label: 'Flow', verb: 'fx', param: 'flow', mode: 'toggle', cell: true, does: 'Top half holds Flow on the cell, the bottom half lets it go' },
  { key: 'fx.echo', label: 'Echo', verb: 'fx', param: 'echo', mode: 'toggle', cell: true, does: 'Top half holds Echo on the cell, the bottom half lets it go' },
  { key: 'fx.zoom', label: 'Zoom', verb: 'fx', param: 'zoom', mode: 'toggle', cell: true, does: 'Top half holds Zoom on the cell, the bottom half lets it go' },
  { key: 'fx.rgb', label: 'RGB split', verb: 'fx', param: 'rgb', mode: 'toggle', cell: true, does: 'Top half holds RGB split on the cell, the bottom half lets it go' },
  { key: 'fx.strobe', label: 'Strobe', verb: 'fx', param: 'strobe', mode: 'toggle', cell: true, does: 'Top half holds Strobe on the cell, the bottom half lets it go' },
  { key: 'fx.shake', label: 'Shake', verb: 'fx', param: 'shake', mode: 'toggle', cell: true, does: 'Top half holds Shake on the cell, the bottom half lets it go' },
  { key: 'fx.off', label: 'No effect', verb: 'fx', param: 'off', mode: 'toggle', cell: true, does: 'Top half hides the cell\'s own effect, the bottom half gives it back' },
  { key: 'fx.amount', label: 'Effect amount', verb: 'fx', param: 'amount', mode: 'value', cell: true, does: 'How strong the effect on the cell is (its own, or the one held on it)' },
];

/** A cell's name on OPVisual's grid, lane first: "L2-3". Lane 1-5 and column 1-10, pulled into range. */
export function opvCellLabel(c: { lane: number; col: number }): string {
  const lane = Math.min(5, Math.max(1, Math.round(c.lane)));
  const col = Math.min(10, Math.max(1, Math.round(c.col)));
  return `L${lane}-${col}`;
}

const ALL = [...OPV_PICTURE_CONTROLS, ...OPV_LAYER_CONTROLS, ...OPV_CELL_FX];

/** The definition behind a lane's `opvControl`, or undefined for a verb this build does not know. */
export function opvControlDef(c: { verb: string; param?: string }): OpvControlDef | undefined {
  return ALL.find((d) => d.verb === c.verb && d.param === c.param);
}
